/**
 * =============================================================================
 * IDS CRM Web - useUnsavedChanges (Batch E UX system)
 * =============================================================================
 *
 * Guards edit forms against silent data loss:
 *  - browser refresh/close triggers the native beforeunload prompt
 *  - in-app link clicks (sidebar, breadcrumbs, mobile nav) are intercepted
 *    and confirmed through the shared ConfirmDialog
 *
 * Programmatic navigate() calls (successful submit, explicit Cancel buttons)
 * are deliberately NOT intercepted - those are intentional exits.
 *
 *   const { guardElement, markDirty, resetDirty } = useUnsavedChanges();
 *   ...onChange: markDirty(); onSubmitSuccess/initial-load: resetDirty();
 *   return (<div>{guardElement} ...</div>);
 *
 * =============================================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import { RESTORE_LIST_STATE } from '../utils/listReturn';

export function useUnsavedChanges() {
  const navigate = useNavigate();
  const dirtyRef = useRef(false);
  const [pendingHref, setPendingHref] = useState(null);
  // A "← List" link (components/BackToList) returns to the list as it was
  // left; that must survive the detour through this dialog.
  const pendingRestoresList = useRef(false);

  const markDirty = useCallback(() => { dirtyRef.current = true; }, []);
  const resetDirty = useCallback(() => { dirtyRef.current = false; }, []);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    // Capture-phase so we beat NavLink/react-router handlers
    const onClick = (event) => {
      if (!dirtyRef.current) return;
      const anchor = event.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/')) return;
      event.preventDefault();
      event.stopPropagation();
      pendingRestoresList.current = anchor.hasAttribute('data-back-to-list');
      setPendingHref(href);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, []);

  const guardElement = pendingHref ? (
    <ConfirmDialog
      title="Discard unsaved changes?"
      message="You have unsaved changes on this form. Leave the page and discard them?"
      confirmLabel="Discard Changes"
      cancelLabel="Keep Editing"
      danger
      onConfirm={() => {
        dirtyRef.current = false;
        const target = pendingHref;
        setPendingHref(null);
        navigate(target, pendingRestoresList.current ? { state: RESTORE_LIST_STATE } : undefined);
      }}
      onCancel={() => setPendingHref(null)}
    />
  ) : null;

  return { guardElement, markDirty, resetDirty };
}

export default useUnsavedChanges;
